import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { GetAllLabelsQueryVariables, Label } from '@buying/library';

import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
    GetAllLabelsDocument,
    GraphClientNames,
    isDefined,
} from '@buying/library';

interface LabelsProviderContextType {
    labels: Array<Label>;
    isLabelsLoading: boolean;
    labelNumbers: Array<Label['labelNumber']>;
}

interface LabelsProviderProps {
    brandNumber: GetAllLabelsQueryVariables['brandNumber'];
}

const LabelsProviderContext = createContext<LabelsProviderContextType | null>(
    null
);

const useLabels = (): LabelsProviderContextType => {
    const context = useContext(LabelsProviderContext);
    if (!context)
        throw new Error('useLabels must be used within a LabelsProvider');
    return context;
};

const LabelsProvider = ({
    brandNumber,
    children,
}: PropsWithChildren<LabelsProviderProps>): ReactElement => {
    const { data, networkStatus } = useQuery(GetAllLabelsDocument, {
        variables: {
            brandNumber: brandNumber,
            labelTypeNumber: 1,
        },
        context: {
            clientName: GraphClientNames['bestone-bi4-buying-basic-data-bff'],
        },
    });

    const isLabelsLoading =
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables;

    const mappedLabels = useMemo(() => {
        if (!data?.labels) return [];
        return data.labels.filter(isDefined);
    }, [data]);

    const labelNumbers = useMemo(
        () => mappedLabels.map((label) => label.labelNumber),
        [mappedLabels]
    );

    const contextValue: LabelsProviderContextType = useMemo(
        () => ({
            labels: mappedLabels,
            isLabelsLoading,
            labelNumbers: labelNumbers,
        }),
        [mappedLabels, isLabelsLoading, labelNumbers]
    );

    return (
        <LabelsProviderContext.Provider value={contextValue}>
            {children}
        </LabelsProviderContext.Provider>
    );
};

export { LabelsProvider, useLabels };
